import type { LegalSection } from "@/components/legal/legal-document";

/**
 * Section order for each legal document, per flavor.
 *
 * Lives outside the page components so the guard test can assert that every key
 * these lists reference exists in the corresponding catalogue — next-intl
 * throws on a missing key, so a typo here is a 500 on /privacy, not a blank
 * heading.
 */

export const PRIVACY_SECTIONS = {
  mint24: [
    { key: "intro" },
    { key: "collect", items: 4 },
    { key: "use", items: 4 },
    { key: "storage" },
    { key: "security" },
    { key: "sharing", items: 3, note: true },
    { key: "rights", items: 4 },
    { key: "cookies" },
    { key: "changes" },
    { key: "contact" },
  ],
  // The US policy adds what American privacy law expects a service to state:
  // who the controller is, the CCPA/CPRA categories, the sub-processors, an
  // explicit no-sale declaration, COPPA, and where data is processed.
  getmintai: [
    { key: "intro" },
    { key: "controller" },
    { key: "collect", items: 5 },
    { key: "sources", items: 3 },
    { key: "use", items: 6 },
    { key: "ai" },
    { key: "sharing", items: 4, note: true },
    { key: "subprocessors", items: 7 },
    { key: "sale" },
    { key: "storage" },
    { key: "security" },
    { key: "rights", items: 6 },
    { key: "exercise" },
    { key: "children" },
    { key: "international" },
    { key: "cookies" },
    { key: "dnt" },
    { key: "shineTheLight" },
    { key: "changes" },
    { key: "contact" },
  ],
} satisfies Record<string, readonly LegalSection[]>;

export const TERMS_SECTIONS = {
  mint24: [
    { key: "intro" },
    { key: "service" },
    { key: "account" },
    { key: "content" },
    { key: "ai" },
    { key: "payment" },
    { key: "prohibited", items: 5 },
    { key: "disclaimer" },
    { key: "liability" },
    { key: "termination" },
    { key: "changes" },
    { key: "contact" },
  ],
  // The US terms add what an American SaaS agreement is expected to carry:
  // eligibility and sanctions, auto-renewal and refund disclosure (California's
  // ARL), a DMCA path, indemnity, dispute resolution, and Wyoming governing law.
  getmintai: [
    { key: "intro" },
    { key: "eligibility" },
    { key: "service" },
    { key: "account" },
    { key: "content" },
    { key: "ai" },
    { key: "license" },
    { key: "payment" },
    { key: "refund" },
    { key: "prohibited", items: 7 },
    { key: "thirdParty" },
    { key: "dmca" },
    { key: "termination" },
    { key: "disclaimer" },
    { key: "liability" },
    { key: "indemnity" },
    { key: "disputes", note: true },
    { key: "governing" },
    { key: "export" },
    { key: "changes" },
    { key: "misc" },
    { key: "contact" },
  ],
} satisfies Record<string, readonly LegalSection[]>;

export const REFUND_SECTIONS = [
  { key: "overview" },
  { key: "merchant" },
  { key: "cancellation" },
  { key: "eligibility", items: 4 },
  { key: "request", items: 3 },
  { key: "processing" },
  { key: "access" },
  { key: "abuse" },
  { key: "contact" },
] as const satisfies readonly LegalSection[];

/**
 * Russian provider's documents have their own structure, in both UI locales.
 *
 * YooKassa's rules for a merchant site (clause 11.8.1) ask for an explicit appeal
 * to customers about protecting their data — hence the advice list under
 * "security", next to what the operator itself does.
 */
export const GRIGORETS_PRIVACY_SECTIONS = [
  { key: "intro" }, { key: "controller" }, { key: "collect", items: 5 },
  { key: "use", items: 4 }, { key: "sharing" }, { key: "international" },
  { key: "storage" }, { key: "security", items: 4 }, { key: "rights" },
  { key: "cookies" }, { key: "contact" },
] as const satisfies readonly LegalSection[];

export const GRIGORETS_TERMS_SECTIONS = [
  { key: "intro" }, { key: "acceptance" }, { key: "service" },
  { key: "delivery" }, { key: "payment" }, { key: "renewal" },
  { key: "trial" },
  { key: "account" }, { key: "prohibited", items: 4 }, { key: "refund" },
  { key: "liability" }, { key: "changes" }, { key: "contact" },
] as const satisfies readonly LegalSection[];

/**
 * The page a payment provider's reviewer reads first, so it carries every
 * item of YooKassa's clause 11.8.1 in one place: seller details, how, when and
 * where the service is provided, keeping proof of payment, and data safety.
 */
export const CONTACT_SECTIONS = [
  { key: "seller" }, { key: "support" }, { key: "delivery" },
  { key: "payments" }, { key: "security", items: 4 },
] as const satisfies readonly LegalSection[];
